import { Dimensions, Image, Linking, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { fp, spH, spV, wp } from "utils/normalizeV2";
import { memo, useCallback, useRef, useState } from "react";

import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import Buttons from "components/Buttons";
import { Colors } from "colors";
import Heading from "components/Heading";
import ImageView from "../../../src/image";

import { Images } from "assets";
import { Link } from "expo-router";
import ListView from "src/components/ListView";
import Paragraph from "components/Pharagraph";
import VText from "components/VText";
import { trackV2 } from "utils/Segment";

const { width } = Dimensions.get("screen");

const Trending = ({ data, gender }) => {
    const { optionName = "", products = [] } = data || {};
    const slicedProducts = products.slice(0, 12);
    const [currentIndex, setCurrentIndex] = useState(0);
    const scrollViewRef = useRef(null);

    const isTrending = optionName === "Trending";
    const itemWidth = spH(210) + 16; // Width of each item plus margin

    const handleScroll = (direction) => {
        if (scrollViewRef.current?.scrollToOffset) {
            const offsetChange = direction === "left" ? -4 : 4;
            const newOffset = Math.max(0, (currentIndex + offsetChange) * itemWidth);
            scrollViewRef.current.scrollToOffset({
                offset: newOffset,
                animated: true,
            });
        }
    };

    const renderCategory = useCallback(
        ({ item }) => (
            <Link
                href={`/product?id=${item?.productId}&brand=${item?.brandName}&product=${item?.productName}&gender=${gender}`}
                style={[styles.categoryItem, isTrending && styles.newArrivalsCategoryItem]}
            >
                <ImageView
                    source={{ uri: item.productImage, priority: ImageView.priority.high }}
                    resizeMode={ImageView.resizeMode.contain}
                    style={styles.productImage}
                    accessibilityLabel={item.productName}
                />
                <Heading type="h3" style={styles.brandText}>
                    {item.brand}
                </Heading>
                <VText
                    style={[styles.productNameText, isTrending && styles.newArrivalsProductNameText]}
                    numberOfLines={2}
                >
                    {item.productName}
                </VText>
            </Link>
        ),
        []
    );

    return (
        <View style={styles.container}>
            <View style={styles.contentContainer}>
                {isTrending && (
                    <View style={styles.leftColumn}>
                        <Heading type="h2" text={optionName} style={styles.headingLeftText} />
                        <Paragraph style={styles.subHeadingText}>
                            The most sought-after designer pieces to secure now and love forever
                        </Paragraph>
                        <Link href={`/shop/trending?optionId=6&gender=${gender}&title=Trending`}>
                            <Buttons
                                textStyle={styles.shopMoreButtonText}
                                containerStyle={styles.shopMoreButton}
                                text="Shop Now"
                                onPress={() => {
                                    const eventData = {
                                        current_section: gender === "female" ? "Women" : "Men",
                                    };
                                    trackV2(ANALYTICS_EVENT_NAMES.SHOP_NOW_CLICKED, eventData);
                                }}
                            />
                        </Link>
                    </View>
                )}
                {isTrending && (
                    <View style={styles.rightColumn}>
                        <ListView
                            onScroll={({ nativeEvent }) => {
                                const offsetX = nativeEvent.contentOffset?.x;
                                setCurrentIndex(Math.floor(offsetX / itemWidth));
                            }}
                            ref={scrollViewRef}
                            type={ListView.Type.Flash}
                            data={slicedProducts}
                            contentContainerStyle={styles.listViewContent}
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            keyExtractor={(item, index) => index.toString()}
                            renderItem={renderCategory}
                            estimatedItemSize={spH(210)}
                            scrollEventThrottle={16}
                            style={styles.listView}
                        />
                        {currentIndex > 0 && (
                            <TouchableOpacity
                                style={[styles.navButton, styles.navButtonLeft]}
                                onPress={() => handleScroll("left")}
                            >
                                <Image
                                    accessibilityLabel="leftArrow"
                                    style={styles.navButtonImage}
                                    source={Images.leftArrow}
                                />
                            </TouchableOpacity>
                        )}
                        {currentIndex + 4 < slicedProducts.length - 1 && (
                            <TouchableOpacity
                                style={[styles.navButton, styles.navButtonRight]}
                                onPress={() => handleScroll("right")}
                            >
                                <Image
                                    accessibilityLabel="rightArrow"
                                    style={styles.navButtonImage}
                                    source={Images.rightArrow}
                                />
                            </TouchableOpacity>
                        )}
                    </View>
                )}
            </View>
        </View>
    );
};

export default memo(Trending);

const styles = StyleSheet.create({
    headingContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        margin: 26,
        alignItem: "center",
    },
    headingLeftText: {
        fontFamily: "LibreBodoni",
        fontWeight: "100",
        textAlign: "left",
        fontSize: fp(32),
        color: Colors.black,
    },
    subHeadingText: {
        marginTop: spV(8),
        fontSize: fp(16),
        fontWeight: "350",
        fontFamily: "JosefinSans",
    },
    navButton: {
        position: "absolute",
        top: "30%",
        borderRadius: 20,
        padding: 10,
    },
    navButtonLeft: {
        left: 0,
    },
    navButtonRight: {
        right: 0,
    },
    navButtonImage: {
        height: spH(24),
        width: spH(24),
    },
    container: {
        marginTop: spV(10),
        marginBottom: spV(50),
        flex: 1,
    },
    contentContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginHorizontal: spH(30),
    },
    leftColumn: {
        width: "22%",
    },
    rightColumn: {
        flex: 1,
        marginLeft: spH(50),
    },
    categoryItem: {
        width: spH(210),
        overflow: "hidden",
    },
    newArrivalsCategoryItem: {
        marginRight: spH(16),
    },
    productImage: {
        height: spH(280),
        width: spH(210),
        marginBottom: 0,
    },
    brandText: {
        fontSize: fp(14),
        color: Colors.black,
        fontFamily: "JosefinSans",
        fontWeight: "500",
        marginTop: spV(16),
        textAlign: "left",
    },
    productNameText: {
        fontSize: fp(14),
        marginTop: spV(2),
        color: Colors.black,
        fontFamily: "JosefinSans",
        fontWeight: "350",
        maxWidth: wp(144),
    },
    newArrivalsProductNameText: {
        maxWidth: (width - 48) / 2,
    },
    shopMoreButton: {
        width: wp(240),
        marginTop: spV(12),
        height: spV(36),
        paddingHorizontal: spH(32),
    },
    listViewContent: {
        paddingRight: spH(16),
    },
    listView: {
        flex: 1,
    },
    shopMoreButtonText: {
        fontSize: fp(16),
        fontFamily: "JosefinSans",
    },
});
